import { Dispatch, SetStateAction } from 'react'

import { OrderHistorySearchCondition } from '@/common/api/modules/orderHistory'
import {
  ListFollowTableHeaderProps,
  makeRectPropsForTableHeadItem,
  useListScrollFollow
} from '@/common/app/list'
import { OrderHistory } from '@/common/model/master/orderHistory'

import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

import OrderHistoryByOrderListRow, {
  GroupedOrder
} from '@/components/container/modules/recommend/orderHistory/table/OrderHistoryByOrderListRow'
import ScrollFollowTable from '@/components/container/utils/list/ScrollFollowTable'

const COLUMNS = [
  '受注日時',
  '顧客特定番号',
  '受注番号',
  '商品ID（明細数）',
  '商品小計（税抜き）',
  '合計金額（税・送料込み）',
  '取得元'
]

interface TheadProps extends ListFollowTableHeaderProps {
  float?: boolean
}

const Thead = ({ float = false, rowRefs, rowRects }: TheadProps) => {
  const rectProps = (index: number) =>
    makeRectPropsForTableHeadItem(rowRefs, rowRects, index, float)

  return (
    <>
      {COLUMNS.map((col, i) => (
        <TableHeadItem key={col} float={float} {...rectProps(i)}>
          <Text size={12} color={TextColor.SECONDARY}>
            {col}
          </Text>
        </TableHeadItem>
      ))}
    </>
  )
}

export const groupOrdersByOrderNumber = (orders: OrderHistory[]): GroupedOrder[] => {
  const grouped = orders.reduce<Record<string, GroupedOrder>>((acc, o) => {
    const key = o.order_number || `_no_order_${o.id}`
    if (!acc[key]) {
      acc[key] = {
        order_number: o.order_number,
        order_datetime: o.order_datetime,
        customer_id: o.customer_id,
        visitor_id: o.visitor_id,
        source: o.source,
        order_total_price: null,
        items: [],
        subtotal: 0
      }
    }
    acc[key].items.push(o)
    acc[key].subtotal += (Number(o.unit_price) || 0) * (Number(o.quantity) || 0)
    if (acc[key].order_total_price == null && o.order_total_price != null) {
      acc[key].order_total_price = o.order_total_price
    }
    return acc
  }, {})
  return Object.values(grouped)
}

interface Props {
  orders: OrderHistory[]
  total: number
  condition: OrderHistorySearchCondition
  setCondition: Dispatch<SetStateAction<OrderHistorySearchCondition>>
  onUpdatePage: (page: number) => void
}

const BY_ORDER_COLUMN_LENGTH = COLUMNS.length

const OrderHistoryByOrderListTable = ({ orders, total, condition, onUpdatePage }: Props) => {
  const groupedOrders = groupOrdersByOrderNumber(orders)
  const listFollowProps = useListScrollFollow(BY_ORDER_COLUMN_LENGTH)
  const { header } = listFollowProps

  const theadProps: TheadProps = { ...header }

  return (
    <ScrollFollowTable
      listFollowProps={listFollowProps}
      tableHead={<Thead {...theadProps} />}
      tableFloatHead={<Thead {...theadProps} float />}
      body={
        <>
          {groupedOrders.map((group) => (
            <OrderHistoryByOrderListRow
              key={group.order_number || `_g_${group.items[0]?.id}`}
              group={group}
            />
          ))}
        </>
      }
      condition={condition}
      onUpdatePage={onUpdatePage}
      total={total}
    />
  )
}

export default OrderHistoryByOrderListTable
